/**
 * @cfleet/auth - useProactiveRefresh
 *
 * Proactive, pre-expiry token renewal for web apps (dispatcher + customer).
 *
 * Why this exists:
 * The api-client middleware only refreshes *reactively* — it waits for a 401,
 * then refreshes and retries. Riding every access token to expiry turns each
 * renewal into a recovery-from-failure, and any hiccup in that recovery logs the
 * user out. Mobile already refreshes ahead of expiry (useForegroundRefresh);
 * this brings the same discipline to web so a 401 stays the rare exception.
 *
 * What it does:
 *  1. Schedules a refresh a fixed buffer BEFORE the access token's real expiry,
 *     keyed off the JWT `exp` claim. NOTE: this is deliberately NOT keyed off
 *     `tokens.expiresAt` — on web that reflects the 7-day *session* lifetime,
 *     while the extracted JWT (better-auth cookie cache) only lives ~1 hour.
 *     Scheduling off `expiresAt` would never fire before the hourly 401.
 *  2. Refreshes on tab refocus when the token is at/near expiry. Browsers
 *     throttle and suspend timers in backgrounded tabs, so the timer alone
 *     can't guarantee a fresh token after a long-idle tab is refocused.
 *  3. NEVER clears auth state on failure. A proactive refresh that fails defers
 *     to the api-client 401 path (the only authoritative logout) and retries on
 *     the next tick / focus.
 *
 * Cross-tab: a best-effort localStorage timestamp guard prevents N open tabs
 * from stampeding the refresh endpoint (and racing refresh-token rotation) at
 * the same instant. Full cross-tab token propagation (BroadcastChannel) is
 * tracked separately as the cross-tab coherence work.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../AuthProvider';
import { getTokenExpiration } from '../utils/token';

/** Refresh this many seconds before the access token's real expiry. */
export const PROACTIVE_REFRESH_BUFFER_SECONDS = 60;

/** Minimum gap between refresh attempts — debounces focus events and bounds
 * the retry rate on persistent failure. Mirrors the mobile hook. */
export const MIN_REFRESH_INTERVAL_MS = 30_000;

/** Short re-evaluation delay after a refresh attempt, giving the post-refresh
 * re-render time to commit the new token before we schedule the next cycle. */
export const POST_REFRESH_REEVAL_MS = 1_000;

/** Upper bound on a single timer delay. Caps far-future schedules so we
 * re-evaluate periodically (surviving clock changes / sleep) and avoid the
 * setTimeout 32-bit overflow for pathologically long-lived tokens. */
export const MAX_TIMER_DELAY_MS = 10 * 60 * 1000;

/** localStorage key used as a best-effort cross-tab refresh mutex. */
export const CROSS_TAB_REFRESH_KEY = 'cfleet.auth.last-proactive-refresh';

/**
 * Milliseconds until a proactive refresh is due for the given access token,
 * or null if the token carries no usable `exp` claim (e.g. empty / cookie-only
 * / non-JWT). Never negative — an already-expired token returns 0 ("due now").
 */
export function computeRefreshDelayMs(
  accessToken: string | null | undefined,
  bufferSeconds: number,
  now: number
): number | null {
  if (!accessToken) return null;
  const expiresAtMs = getTokenExpiration(accessToken);
  if (expiresAtMs === null) return null;
  return Math.max(0, expiresAtMs - bufferSeconds * 1000 - now);
}

/**
 * Whether a tab-refocus should trigger a refresh. True only when the token is
 * within the buffer of expiry. A fully expired token bypasses the debounce
 * (the next action would otherwise 401); a merely near-expiry token respects it.
 */
export function shouldRefreshOnFocus(
  accessToken: string | null | undefined,
  lastRefreshTime: number,
  now: number,
  bufferSeconds: number = PROACTIVE_REFRESH_BUFFER_SECONDS,
  minIntervalMs: number = MIN_REFRESH_INTERVAL_MS
): boolean {
  if (!accessToken) return false;
  const expiresAtMs = getTokenExpiration(accessToken);
  if (expiresAtMs === null) return false;
  if (expiresAtMs - bufferSeconds * 1000 > now) return false; // comfortably fresh
  if (expiresAtMs <= now) return true; // fully expired → bypass debounce
  return now - lastRefreshTime > minIntervalMs;
}

/**
 * Best-effort cross-tab stampede guard. Returns true if this tab may proceed
 * with a refresh (and records the claim), false if another tab refreshed within
 * the window. Storage errors never block a refresh.
 */
export function claimCrossTabRefresh(
  now: number,
  minIntervalMs: number = MIN_REFRESH_INTERVAL_MS
): boolean {
  try {
    const raw = window.localStorage.getItem(CROSS_TAB_REFRESH_KEY);
    const last = raw ? Number.parseInt(raw, 10) : 0;
    if (last && now - last < minIntervalMs) {
      return false;
    }
    window.localStorage.setItem(CROSS_TAB_REFRESH_KEY, String(now));
    return true;
  } catch {
    // Storage disabled (private mode, quota, SSR) — don't let it block renewal.
    return true;
  }
}

const clampDelay = (delay: number): number => Math.min(Math.max(delay, 0), MAX_TIMER_DELAY_MS);

/**
 * Wire proactive refresh into a web app. Call once, inside an AuthProvider
 * (the provider already does this, so dispatcher + customer inherit it).
 * No-ops outside the browser and while unauthenticated.
 */
export function useProactiveRefresh(): void {
  const { refreshToken, isAuthenticated, tokens } = useAuth();

  // Latest access token, read by the self-rescheduling timer without forcing it
  // to tear down and rebuild on every token change.
  const accessTokenRef = useRef<string | null | undefined>(tokens?.accessToken);
  accessTokenRef.current = tokens?.accessToken;

  const lastAttemptRef = useRef<number>(0);
  const inFlightRef = useRef<Promise<void> | null>(null);

  // Serialize refreshes within this tab, guard across tabs, and — critically —
  // never clear auth state on failure.
  const performRefresh = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) return inFlightRef.current;

    const now = Date.now();
    lastAttemptRef.current = now; // record even on skip so we back off, not hot-loop

    if (!claimCrossTabRefresh(now)) {
      // Another tab is handling renewal this window.
      return;
    }

    const promise = refreshToken()
      .catch((err: unknown) => {
        // A proactive refresh must never tear down the session. Genuine expiry
        // is handled by the api-client 401 path; transient failures are retried
        // by the next timer tick or focus event.
        const message = err instanceof Error ? err.message : String(err);
        console.warn('[useProactiveRefresh] Proactive token refresh failed:', message);
      })
      .finally(() => {
        inFlightRef.current = null;
      });

    inFlightRef.current = promise;
    return promise;
  }, [refreshToken]);

  // Schedule-to-expiry timer (a single self-rescheduling loop).
  // Gated on `document` so this stays web-only: React Native polyfills `window`
  // but not `document`, and mobile has its own useForegroundRefresh.
  useEffect(() => {
    if (typeof document === 'undefined' || !isAuthenticated) return;

    let timerId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = (delay: number): void => {
      if (cancelled) return;
      timerId = setTimeout(run, clampDelay(delay));
    };

    const run = async (): Promise<void> => {
      if (cancelled) return;
      const now = Date.now();
      const delay = computeRefreshDelayMs(
        accessTokenRef.current,
        PROACTIVE_REFRESH_BUFFER_SECONDS,
        now
      );

      // No schedulable JWT yet (cookie-only / empty) — re-check later.
      if (delay === null) {
        schedule(MAX_TIMER_DELAY_MS);
        return;
      }

      // Not due yet — wait until the buffer-before-expiry mark.
      if (delay > 0) {
        schedule(delay);
        return;
      }

      // Due. Respect the debounce so a non-extending refresh can't hot-loop.
      const sinceLast = now - lastAttemptRef.current;
      if (sinceLast < MIN_REFRESH_INTERVAL_MS) {
        schedule(MIN_REFRESH_INTERVAL_MS - sinceLast);
        return;
      }

      await performRefresh();
      if (cancelled) return;
      // Re-evaluate shortly: on success the new token pushes the next refresh
      // far out; on failure/skip the debounce above paces the retry.
      schedule(POST_REFRESH_REEVAL_MS);
    };

    void run();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [isAuthenticated, performRefresh]);

  // Tab-refocus safety net for throttled/suspended background timers.
  useEffect(() => {
    if (typeof document === 'undefined' || !isAuthenticated) return;

    const handleVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (shouldRefreshOnFocus(accessTokenRef.current, lastAttemptRef.current, Date.now())) {
        void performRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAuthenticated, performRefresh]);
}
